/** DB-backed composition templates — transition banners & talking heads layouts. */

import api from '../../api';

export const TEMPLATE_TYPES = {
  TRANSITION_BANNER: 'transition_banner',
  TALKING_HEADS: 'talking_heads',
  INTRO_TEASER: 'intro_teaser',
};

const accountBannerKey = (user) =>
  `composition_transition_banner_${user?.email || user?._id || user?.id || 'guest'}`;

const accountTeaserKey = (user) =>
  `composition_intro_teaser_${user?.email || user?._id || user?.id || 'guest'}`;

const channelBannerKey = (user, channelId) =>
  `${accountBannerKey(user)}_channel_${channelId}`;

const channelTeaserKey = (user, channelId) =>
  `${accountTeaserKey(user)}_channel_${channelId}`;

export function buildBannerPayload(presetId, config, enabled) {
  return {
    presetId: presetId || 'custom',
    config,
    enabled: enabled !== false,
  };
}

export function parseBannerPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (!payload.config) return null;
  return {
    presetId: payload.presetId || 'custom',
    config: payload.config,
    enabled: payload.enabled !== false,
  };
}

export function buildTeaserPayload(presetId, config) {
  return {
    presetId: presetId || 'custom',
    config,
  };
}

export function parseTeaserPayload(payload) {
  if (!payload || typeof payload !== 'object' || !payload.config) return null;
  return {
    presetId: payload.presetId || 'custom',
    config: payload.config,
  };
}

export function normalizeTalkingHeadsTemplate(doc) {
  if (!doc) return doc;
  const layout_config = doc.layout_config || doc.payload?.layout_config;
  return { ...doc, layout_config };
}

export async function listCompositionTemplates({ type, channelId } = {}) {
  const params = {};
  if (type) params.type = type;
  if (channelId) params.channel_id = channelId;
  const res = await api.get('/composition/templates', { params });
  return res.data?.templates || [];
}

export async function getDefaultCompositionTemplate({ type, channelId } = {}) {
  const params = { type };
  if (channelId) params.channel_id = channelId;
  const res = await api.get('/composition/templates/default', { params });
  return res.data?.template || null;
}

export async function saveCompositionTemplate({
  type,
  name,
  payload,
  channelId,
  templateId,
  setAsDefault = false,
}) {
  const body = {
    type,
    name,
    payload,
    set_as_default: !!setAsDefault,
  };
  if (channelId) body.channel_id = channelId;
  if (templateId) body.template_id = templateId;
  const res = await api.post('/composition/templates', body);
  return res.data;
}

export async function deleteCompositionTemplate(templateId) {
  await api.delete(`/composition/templates/${templateId}`);
}

/** Offline cache — used only when API is unavailable. */
export function cacheAccountBannerDefault(user, payload) {
  try {
    localStorage.setItem(accountBannerKey(user), JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

export function loadCachedAccountBannerDefault(user) {
  try {
    const raw = localStorage.getItem(accountBannerKey(user));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function cacheChannelBanner(user, channelId, payload) {
  if (!channelId) return;
  try {
    localStorage.setItem(channelBannerKey(user, channelId), JSON.stringify(payload));
  } catch {
    /* ignore quota */
  }
}

export function loadCachedChannelBanner(user, channelId) {
  if (!channelId) return null;
  try {
    const raw = localStorage.getItem(channelBannerKey(user, channelId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function cacheAccountTeaserDefault(user, payload) {
  try {
    localStorage.setItem(accountTeaserKey(user), JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function loadCachedAccountTeaserDefault(user) {
  try {
    const raw = localStorage.getItem(accountTeaserKey(user));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function cacheChannelTeaser(user, channelId, payload) {
  if (!channelId) return;
  try {
    localStorage.setItem(channelTeaserKey(user, channelId), JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function loadCachedChannelTeaser(user, channelId) {
  if (!channelId) return null;
  try {
    const raw = localStorage.getItem(channelTeaserKey(user, channelId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
